import React, { useEffect, useState } from "react";
import classes from "./Table.module.scss";
import Chevron from "../Icons/Chevron";
import { isFrappeOrJotFormClass } from "../../../utils/helpers";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export interface ColumnHeader {
  label: string;
  key: string;
  type?: string;
  style?: React.CSSProperties;
}

interface TableProps {
  columnHeaders: ColumnHeader[];
  tableData: any[];
  pagination?: boolean;
  rowsPerPage?: number;
  noDataMessage?: string;
  onPageChange?: (page: number) => void;
  totalItems?: number;
  loading?: boolean;
  currentPage?: number;
}

const Table = ({
  columnHeaders,
  tableData,
  pagination = true,
  rowsPerPage = 5,
  noDataMessage = "No Entries to show",
  totalItems = 0,
  onPageChange,
  loading = false,
  currentPage: controlledPage,
}: TableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    setTotalPages(Math.ceil(totalItems / rowsPerPage));
  }, [totalItems, rowsPerPage]);

  // Sync the highlighted page to the page reported by the parent
  useEffect(() => {
    if (controlledPage != null) setCurrentPage(controlledPage);
  }, [controlledPage]);

  const leftBtnClickHandler = () => {
    setCurrentPage((prevState) => {
      if (prevState === 1) return 1;
      const newPage = prevState - 1;
      onPageChange?.(newPage);
      return newPage;
    });
  };

  const rightBtnClickHandler = () => {
    setCurrentPage((prevState) => {
      if (prevState === totalPages) return prevState;
      const newPage = prevState + 1;
      onPageChange?.(newPage);
      return newPage;
    });
  };

  const pageBtnClickHandler = (page: number) => () => {
    setCurrentPage(page);
    onPageChange?.(page);
  };

  const btnClickHandler = (btnLink: string) => () => {
    window.open(btnLink, "_blank");
  };

  return (
    <div className={classes["table-container"]}>
      <table>
        <thead>
          <tr>
            {columnHeaders.map((header) => (
              <th key={header.key} style={{ whiteSpace: "nowrap" }}>
                {header.label}
              </th>
            ))}
          </tr>
        </thead>
        {loading ? (
          <tbody>
            {Array.from({ length: rowsPerPage }).map((_, rowIndex) => (
              <tr key={rowIndex}>
                {columnHeaders.map((header) => (
                  <td key={header.key} style={header.style}>
                    <Skeleton />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        ) : tableData && tableData.length > 0 ? (
          <tbody>
            {tableData.map((row) => (
              <tr key={row.id}>
                {columnHeaders.map((header) => (
                  <td key={header.key} style={header.style}>
                    {header?.type === "link" ? (
                      <button
                        className={`${classes["register-button"]} ${!row[header.key]?.link && isFrappeOrJotFormClass(row[header.key]?.formId)}`}
                        onClick={row[header.key]?.link ? btnClickHandler(row[header.key]?.link) : undefined}
                      >
                        {row[header.key]?.btnTitle}
                        <Chevron color="#FF7361" width={8} height={13} />
                      </button>
                    ) : (
                      row[header.key]
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td colSpan={columnHeaders.length} className={classes["no-data-msg"]}>
                {noDataMessage}
              </td>
            </tr>
          </tbody>
        )}
      </table>

      {pagination && (
        <div className={classes["pagination-container"]}>
          <button className={`${classes["pagination-left-button"]} ${currentPage === 1 ? classes["button-disabled"] : ""}`} onClick={leftBtnClickHandler}>
            <Chevron />
          </button>
          <div className={classes["pagination-page-buttons-container"]}>
            {Array(totalPages)
              .fill(0)
              .map((_, index) => (
                <button
                  key={index}
                  className={`${classes["pagination-page-button"]} ${currentPage === index + 1 ? classes["pagination-page-button-active"] : ""}`}
                  style={
                    currentPage > 3
                      ? {
                          transform: `translateX(-${(currentPage - 2) * 64}px)`,
                        }
                      : {}
                  }
                  onClick={index + 1 != currentPage ? pageBtnClickHandler(index + 1) : () => {}}
                >
                  {index + 1}
                </button>
              ))}
          </div>
          <button
            className={`${classes["pagination-right-button"]} ${currentPage === totalPages ? classes["button-disabled"] : ""}`}
            onClick={rightBtnClickHandler}
          >
            <Chevron />
          </button>
        </div>
      )}
    </div>
  );
};

export default Table;
